/**
 * People area: Employment Hero workforce data, eTrainu training, workplace
 * compliance, the signed-in user's own compliance, trends and the EH
 * integration card. Everything derives from world.ts STAFF via
 * data/people/*, so headcount, leavers, training and compliance reconcile.
 *
 * Exported generators (pure, deterministic per day; import these instead of
 * re-deriving so every surface shows identical numbers):
 *
 *   ehStats(board = false)              GET /api/eh-data/stats body (synced). `board` applies the BoardProjection allowlist.
 *   ehSummary()                         ehStats(false), typed.
 *   ehTrends(q, days?, board = false)   GET /api/trends/employment_hero body; ehTrendRow(day) for one 'YYYY-MM-DD'.
 *   turnoverRate12m(day, type?)         12-month rolling turnover % as at a day (type: fulltime|parttime|casual).
 *   turnover12m(day)                    the same rate with its leavers, average headcount and window.
 *   trainingStats(board = false)        GET /api/training/stats body; trainingSummary() typed full version.
 *   trainingTrends(q, days?)            GET /api/trends/training body; trainingTrendRow(day) for one day.
 *   trainingRecords()                   every eTrainu record (staff × course) behind the stats.
 *   complianceByCategory(board = false) the `compliance_by_category` block of ehStats.
 *   complianceSummary()                 category rates (workforce/mandatory/essential), totals, NDIS Worker Screening
 *                                       Clearance counts and items below target.
 *   complianceExport(category)          GET /api/eh-data/compliance-export body.
 *   myCompliance(persona, syncedAt)     GET /api/me/compliance body.
 *   peopleSummary(q: Quarter | null)    one object for the board dashboard (`active_staff`, `training_completion_rate`,
 *                                       `training_in_progress`, `turnover_rate_12m`), the CEO report and the assistant:
 *                                       current point-in-time KPIs + hires/leavers/sick leave/payroll within the quarter.
 *   headcountInQuarter(q)               staff employed at any point in the quarter.
 *   SCHADS_REFERENCE, payRunDates()     payroll constants.
 *
 * Routes: /api/eh-data/{stats,sync,compliance-export}, /api/training/{stats,sync},
 * /api/trends/{employment_hero,training}, /api/me/compliance,
 * /api/eh/{status,authorize,share-link,disconnect,callback,external-authorize/:token}.
 * GET /api/sync/status/:type lives in core.ts.
 */
import { Route } from '../router';
import { iso, now, quarterParam } from '../clock';
import { getState, lastSyncedAt, queueSync, setState } from '../state';
import { ORG } from '../world';
import { complianceExport, myCompliance } from '../data/people/compliance';
import { trainingStats, trainingTrends } from '../data/people/training';
import { EH_NOT_CONNECTED, ehStats, ehTrends } from '../data/people/workforce';
import { uuidFor } from '../data/people/common';

export { complianceByCategory, complianceExport, complianceSummary, myCompliance } from '../data/people/compliance';
export { trainingRecords, trainingStats, trainingSummary, trainingTrendRow, trainingTrends } from '../data/people/training';
export { SCHADS_REFERENCE, ehStats, ehSummary, ehTrendRow, ehTrends, headcountInQuarter, payRunDates, peopleSummary, turnover12m, turnoverRate12m } from '../data/people/workforce';

const EH_CONNECTED = 'people:eh:connected';
const ehConnected = (): boolean => getState(EH_CONNECTED, () => true);

export const PEOPLE_ROUTES: Route[] = [
    // --- Employment Hero data -------------------------------------------------
    { method: 'GET', path: '/api/eh-data/stats', handler: ctx => (ehConnected() ? ehStats(ctx.board) : EH_NOT_CONNECTED) },
    { method: 'POST', path: '/api/eh-data/sync', handler: () => queueSync('employment_hero', 'Employment Hero') },
    { method: 'GET', path: '/api/eh-data/compliance-export', handler: ctx => complianceExport(ctx.query.get('category')) },

    // --- eTrainu training -----------------------------------------------------
    { method: 'GET', path: '/api/training/stats', handler: ctx => trainingStats(ctx.board) },
    { method: 'POST', path: '/api/training/sync', handler: () => queueSync('training', 'Training') },

    // --- Daily history --------------------------------------------------------
    { method: 'GET', path: '/api/trends/employment_hero', handler: ctx => ehTrends(quarterParam(ctx.query), ctx.query.get('days'), ctx.board) },
    { method: 'GET', path: '/api/trends/training', handler: ctx => trainingTrends(quarterParam(ctx.query), ctx.query.get('days')) },

    // --- My compliance --------------------------------------------------------
    { method: 'GET', path: '/api/me/compliance', handler: ctx => myCompliance(ctx.persona, lastSyncedAt('employment_hero')) },

    // --- Settings → Integrations (Employment Hero card) -----------------------
    {
        method: 'GET',
        path: '/api/eh/status',
        handler: () =>
            ehConnected()
                ? {
                      connected: true,
                      organisation_name: ORG.name,
                      organisation_id: 'org_npy_demo',
                      expires_at: iso(new Date(now().getTime() + 52 * 60000)),
                      scopes: 'urn:mainapp:organisations:read urn:mainapp:employees:read urn:mainapp:leave_requests:read urn:mainapp:certifications:read urn:mainapp:payroll:read',
                  }
                : { connected: false },
    },
    {
        method: 'GET',
        path: '/api/eh/authorize',
        handler: () => {
            setState(EH_CONNECTED, true);
            return { authorize_url: '/settings/integrations?eh_auth=success' };
        },
    },
    {
        method: 'POST',
        path: '/api/eh/share-link',
        handler: () => ({ url: `https://api.${ORG.domain}/api/eh/external-authorize/${uuidFor(`eh-share:${Math.floor(now().getTime() / 3600000)}`).replace(/-/g, '')}` }),
    },
    {
        method: 'POST',
        path: '/api/eh/disconnect',
        handler: () => {
            setState(EH_CONNECTED, false);
            return { success: true };
        },
    },
    // Browser navigations in the real app (never XHR); answered so nothing errors if probed.
    { method: 'GET', path: '/api/eh/callback', handler: () => ({ success: true }) },
    { method: 'GET', path: '/api/eh/external-authorize/:token', handler: () => ({ authorize_url: '/settings/integrations?eh_auth=success' }) },
];
